<template>
  <v-menu
    v-model="active"
    :close-on-content-click="false"
    :location="isRTL ? 'bottom end' : 'bottom start'"
    offset="4"
    :attach="false"
    content-class="language-dropdown-menu"
    :class="{ 'rtl-menu': isRTL }"
  >
    <template v-slot:activator="{ props }">
      <v-btn
        v-bind="props"
        variant="outlined"
        class="text-none language-selector-btn" 
        style="background-color: #F5F7FA;"
        :prepend-icon="`flag-icon flag-icon-${selectedLanguage?.flag}`"
      >
        {{ selectedLanguage?.code }}
        <v-icon :class="isRTL ? 'mr-1' : 'ml-1'">mdi-chevron-down</v-icon>
      </v-btn>
    </template>

    <v-card min-width="250">
      <v-list density="compact">
        <v-list-item
          v-for="lang in languages"
          :key="lang.id"
          @click="selectLanguage(lang)"
          class="cursor-pointer"
        >
          <template v-slot:prepend>
            <i :class="`flag-icon flag-icon-${lang.flag} m-rtl`" ></i>
          </template>
          
          <v-list-item-title>
            {{ lang.name }}
          </v-list-item-title>
          
          <template v-slot:append>
            <span class="text-medium-emphasis text-caption">
              {{ lang.code }}
            </span>
          </template>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script lang="ts" setup>
import { ref, onMounted, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { i18n } from '@/main';
import language from "@/API/Language/Language";

const { t } = useI18n();

interface Language {
  id: string;
  code: string;
  name: string;
  order: number;
  rtl: string;
  flag: string;
}

const active = ref<boolean>(false);
const languages = ref<Language[]>([]);
const selectedLanguage = ref<Language | null>(null);
const languageApi = new language();

// Reactive RTL state
const isRTL = ref<boolean>(
  document.documentElement.dir === 'rtl' ||
  localStorage.getItem('selectedLanguageCode') === 'ar' ||
  document.documentElement.getAttribute('lang') === 'ar'
);

async function fetchLanguages() {
  try {
    const response = await languageApi.getAll() as Language[];
    languages.value = response.sort((a: Language, b: Language) => 
      Number(a.order) - Number(b.order)
    );
    
    // Load saved language from localStorage if available
    const savedLangCode = localStorage.getItem('selectedLanguageCode');
    const savedLang = languages.value.find(lang => lang.code.toLowerCase() === savedLangCode?.toLowerCase());
    if (savedLang) {
      i18n.global.locale.value = savedLang.code.toLowerCase();
      selectLanguage(savedLang, false);
    } else if (!selectedLanguage.value && languages.value.length > 0) {
      selectLanguage(languages.value[0], false);
    }
  } catch (error) {
    console.error('Error fetching languages:', error);
  }
}

function selectLanguage(language: Language, refresh = true) {
  active.value = false;
  (i18n.global.locale as any).value = language.code.toLowerCase();
  selectedLanguage.value = language;
  
  // Save selected language code to localStorage
  localStorage.setItem('selectedLanguageCode', language.code.toLowerCase());
  
  // Update RTL state
  const isRtlLanguage = language.rtl === 'true';
  isRTL.value = isRtlLanguage;
  
  // Set direction based on RTL flag
  document.documentElement.dir = isRtlLanguage ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('lang', language.code.toLowerCase());
  
  // Update body class for additional styling if needed
  if (isRtlLanguage) {
    document.body.classList.add('rtl');
    document.body.classList.remove('ltr');
  } else {
    document.body.classList.add('ltr');
    document.body.classList.remove('rtl');
  }
  
  if (refresh) {
    window.location.reload();
  }
}

onMounted(() => {
  fetchLanguages();
  updateRTLState();
  
  // Set up mutation observer to watch for direction changes
  const observer = new MutationObserver(() => {
    updateRTLState();
  });
  
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['dir', 'lang']
  });
});

// Force position the dropdown based on RTL/LTR when it opens
watch(active, (newVal) => {
  if (newVal) {
    nextTick(() => {
      const dropdownElement = document.querySelector('.language-dropdown-menu .v-overlay__content') as HTMLElement;
      if (dropdownElement) {
        dropdownElement.style.position = 'fixed';
        dropdownElement.style.transform = 'none';
        dropdownElement.style.zIndex = '9999';
        
        if (isRTL.value) {
          dropdownElement.style.left = '20px';
          dropdownElement.style.right = 'auto';
        } else {
          dropdownElement.style.right = '20px';
          dropdownElement.style.left = 'auto';
        }
      }
    });
  }
});

// Update RTL state when document direction changes
const updateRTLState = () => {
  const newRTL = document.documentElement.dir === 'rtl' ||
                 localStorage.getItem('selectedLanguageCode') === 'ar';
  if (newRTL !== isRTL.value) {
    isRTL.value = newRTL;
  }
};

// Watch for direction changes
watch(isRTL, (newVal) => {
  // Force update dropdown position if it's open
  if (active.value) {
    nextTick(() => {
      const dropdownElement = document.querySelector('.language-dropdown-menu .v-overlay__content') as HTMLElement;
      if (dropdownElement) {
        if (newVal) {
          dropdownElement.style.left = '20px';
          dropdownElement.style.right = 'auto';
        } else {
          dropdownElement.style.right = '20px';
          dropdownElement.style.left = 'auto';
        }
      }
    });
  }
});
</script>

<style>
.cursor-pointer {
  cursor: pointer;
}

/* Ensure flag icons display properly */
.flag-icon {
  width: 20px;
  height: 15px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  display: inline-block;
}

.v-btn--variant-outlined {
  border: none !important;
}

/* Language dropdown positioning - LTR */
.language-dropdown-menu .v-overlay__content {
  position: fixed !important;
  right: 20px !important;
  left: auto !important;
  transform: none !important;
  top: auto !important;
  z-index: 9999 !important;
}

/* Language dropdown positioning - RTL */
[dir="rtl"] .language-dropdown-menu .v-overlay__content {
  position: fixed !important;
  left: 20px !important;
  right: auto !important;
  transform: none !important;
  top: auto !important;
  z-index: 9999 !important;
}

/* RTL Menu class for additional targeting */
.rtl-menu .v-overlay__content {
  left: 20px !important;
  right: auto !important;
}
/* RTL List Item Layout */
[dir="rtl"] .v-list-item {
  direction: rtl;
  text-align: right;
}

[dir="rtl"] .v-list-item-title {
  text-align: right;
  flex-direction: row !important;
}

[dir="rtl"] .v-list-item .v-list-item__prepend {
  margin-left: 12px;
  margin-right: 0;
}

[dir="rtl"] .v-list-item .v-list-item__append {
  margin-left: 0;
  margin-right: 12px;
}

/* Flag icon margins */
.m-rtl {
  margin-left: 0px;
  margin-right: 8px;
}

[dir="rtl"] .m-rtl {
  margin-left: 8px;
  margin-right: 0px;
}

/* Button RTL support */
[dir="rtl"] .v-btn {
  flex-direction: row-reverse;
}

[dir="rtl"] .v-btn .v-icon {
  margin-left: 0;
  margin-right: 4px;
}

[dir="rtl"] .v-btn .mdi-chevron-down {
  transform: scaleX(-1);
}

/* Card RTL support */
[dir="rtl"] .v-card {
  direction: rtl;
}

/* Text alignment for RTL */
[dir="rtl"] .text-caption {
  text-align: right;
}

/* Language selector button RTL positioning */
.language-selector-btn {
  position: relative;
}

[dir="rtl"] .language-selector-btn {
  direction: rtl;
}

[dir="rtl"] .language-selector-btn .v-btn__prepend {
  margin-left: 8px;
  margin-right: 0;
}

[dir="rtl"] .language-selector-btn .v-btn__append {
  margin-left: 0;
  margin-right: 8px;
}

/* Override Vuetify's default margins for RTL */
[dir="rtl"] .ml-1 {
  margin-left: 0 !important;
  margin-right: 0.25rem !important;
}

[dir="rtl"] .mr-1 {
  margin-right: 0 !important;
  margin-left: 0.25rem !important;
}

/* Language dropdown menu container */
.language-dropdown-menu {
  z-index: 9999 !important;
}

/* Responsive positioning */
@media (max-width: 768px) {
  .language-dropdown-menu .v-overlay__content {
    right: 10px !important;
  }
  
  [dir="rtl"] .language-dropdown-menu .v-overlay__content {
    left: 10px !important;
    right: auto !important;
  }
}

</style>